import * as React from 'react';
import { DetectedPdfField, PdfFieldType } from '@/interfaces';
import { formatPhoneRemove } from '@/lib';
import { contactFormatError } from '../lib/contactFieldFormat';
import {
	ClientSignDataType,
	isValidatedClientDataType,
	mockDataTypeForField,
} from '../lib/mockDataTypes';
import { SettingsLookup } from './useClientSettingsLookup';

export type ClientSignFieldValue = string | boolean;

export interface ClientSignFieldState {
	dataType: ClientSignDataType;
	label: string;
	fieldName: string;
	fieldType: PdfFieldType;
	value: ClientSignFieldValue;
	readOnly: boolean;
	questionId?: string;
	/** Client answer already painted on the PDF. Hidden until the field is edited. */
	paintedValue?: string;
}

export type ClientSignFieldsMap = Record<string, ClientSignFieldState>;

const defaultValueForType = (fieldType: PdfFieldType): ClientSignFieldValue => {
	if (fieldType === 'checkbox') return false;
	return '';
};

const isRadioComplete = (
	fieldId: string,
	fields: ClientSignFieldsMap
): boolean => {
	const field = fields[fieldId];
	if (!field || field.fieldType !== 'radio') return false;
	return Object.values(fields).some(
		(other) =>
			other.fieldType === 'radio' &&
			other.fieldName === field.fieldName &&
			typeof other.value === 'string' &&
			other.value.trim().length > 0
	);
};

const isFieldComplete = (
	fieldId: string,
	fields: ClientSignFieldsMap
): boolean => {
	const field = fields[fieldId];
	if (!field) return false;
	if (field.fieldType === 'checkbox') return true;
	if (field.fieldType === 'radio') return isRadioComplete(fieldId, fields);
	if (typeof field.value === 'boolean') return true;
	if (field.value.trim().length === 0) return false;
	return contactFormatError(field.dataType, field.value) === null;
};

const digitsOnly = (value: string): string =>
	formatPhoneRemove(value).replace(/\D/g, '');

export const normalizeForCompare = (
	dataType: ClientSignDataType,
	value: string
): string => {
	const trimmed = value.trim();
	if (dataType === 'PHONE') return digitsOnly(trimmed);
	return trimmed.toLowerCase();
};

export const valuesMismatch = (
	dataType: ClientSignDataType,
	entered: ClientSignFieldValue,
	stored: string | undefined
): boolean => {
	if (!isValidatedClientDataType(dataType)) return false;
	if (typeof entered === 'boolean') return false;
	const storedValue = stored?.trim() ?? '';
	if (!storedValue) return false;
	return (
		normalizeForCompare(dataType, entered) !==
		normalizeForCompare(dataType, storedValue)
	);
};

export interface UseClientSignFieldsResult {
	fields: ClientSignFieldsMap;
	setFieldValue: (fieldId: string, value: ClientSignFieldValue) => void;
	allComplete: boolean;
	hasDetectedFields: boolean;
	mismatchedFieldIds: string[];
}

export const useClientSignFields = (
	detectedFields: DetectedPdfField[],
	settings: SettingsLookup,
	dataTypeByFieldName: Record<string, ClientSignDataType> = {},
	previousResponseByFieldName: Record<string, string> = {},
	questionIdByFieldName: Record<string, string> = {}
): UseClientSignFieldsResult => {
	const [fields, setFields] = React.useState<ClientSignFieldsMap>({});

	React.useEffect(() => {
		if (detectedFields.length === 0) return;

		setFields((current) => {
			let hasNewEntries = false;
			const next = { ...current };

			detectedFields.forEach((detected) => {
				const mappedType = dataTypeByFieldName[detected.fieldName];
				const fieldType: PdfFieldType =
					detected.fieldType === 'signature' || mappedType === 'SIGNATURE'
						? 'signature'
						: detected.fieldType;
				const existing = next[detected.id];
				const previousResponse =
					previousResponseByFieldName[detected.fieldName];
				if (existing) {
					const typeChanged =
						existing.fieldType !== fieldType ||
						(fieldType === 'signature' && existing.dataType !== 'SIGNATURE');
					const blank =
						typeof existing.value === 'string' &&
						existing.value.trim().length === 0;
					const shouldSeed = previousResponse !== undefined && blank;
					const missingPainted =
						previousResponse !== undefined &&
						existing.paintedValue === undefined;
					if (
						typeChanged ||
						existing.readOnly ||
						shouldSeed ||
						missingPainted
					) {
						hasNewEntries = true;
						next[detected.id] = {
							...existing,
							fieldType,
							dataType:
								fieldType === 'signature' ? 'SIGNATURE' : existing.dataType,
							readOnly: false,
							value: shouldSeed ? previousResponse : existing.value,
							paintedValue: existing.paintedValue ?? previousResponse,
						};
					}
					return;
				}
				hasNewEntries = true;
				next[detected.id] = {
					dataType:
						fieldType === 'signature'
							? 'SIGNATURE'
							: mappedType ?? mockDataTypeForField(detected),
					label: detected.label,
					fieldName: detected.fieldName,
					fieldType,
					value:
						previousResponse ??
						detected.defaultValue ??
						defaultValueForType(fieldType),
					readOnly: false,
					paintedValue: previousResponse,
					questionId: questionIdByFieldName[detected.fieldName],
				};
			});

			return hasNewEntries ? next : current;
		});
	}, [
		detectedFields,
		dataTypeByFieldName,
		previousResponseByFieldName,
		questionIdByFieldName,
	]);

	const setFieldValue = React.useCallback(
		(fieldId: string, value: ClientSignFieldValue) => {
			setFields((current) => {
				const existing = current[fieldId];
				if (!existing) return current;
				const next = { ...current, [fieldId]: { ...existing, value } };
				if (existing.fieldType === 'radio') {
					Object.keys(next).forEach((id) => {
						if (id === fieldId) return;
						const other = next[id];
						if (
							other.fieldType === 'radio' &&
							other.fieldName === existing.fieldName
						) {
							next[id] = { ...other, value: '' };
						}
					});
				}
				return next;
			});
		},
		[]
	);

	const editableFieldIds = React.useMemo(
		() =>
			detectedFields
				.map((field) => field.id)
				.filter((id) => !fields[id]?.readOnly),
		[detectedFields, fields]
	);

	const hasDetectedFields = editableFieldIds.length > 0;

	const allComplete = React.useMemo(() => {
		if (!hasDetectedFields) return true;
		return editableFieldIds.every((id) => {
			const field = fields[id];
			if (!field) return false;
			return isFieldComplete(id, fields);
		});
	}, [editableFieldIds, fields, hasDetectedFields]);

	const mismatchedFieldIds = React.useMemo(() => {
		return editableFieldIds.filter((id) => {
			const field = fields[id];
			if (!field) return false;
			const stored = settings[field.dataType];
			return valuesMismatch(field.dataType, field.value, stored);
		});
	}, [editableFieldIds, fields, settings]);

	return {
		fields,
		setFieldValue,
		allComplete,
		hasDetectedFields,
		mismatchedFieldIds,
	};
};
